import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import NoteForm from "./NoteForm";
import DeleteAnnotationConfirmForm from "./DeleteAnnotationConfirmForm";
import {
  Annotation,
  getAnnotationLabel,
} from "../utils/annotator/annotationsHelper";
import "./NoteModal.scss";

type NoteModalMode = "create" | "edit";

const TITLES: Record<NoteModalMode, string> = {
  create: "Add note",
  edit: "Edit note",
};

interface NoteModalProps {
  /** Which UI variant to render. */
  mode: NoteModalMode;
  /** Annotation being edited (omitted for "create"). */
  annotation?: Annotation;
  /** Invoked when the user saves a new note ("create"). Receives trimmed note text (or `null` if blank) and the chosen highlight color. */
  onSave?: (note: string | null, colorValue: string) => void;
  /** Invoked when the user saves an edited note ("edit"). Receives trimmed note text (or `null` if blank) and the chosen highlight color. */
  onEdit?: (note: string | null, colorValue: string) => void;
  /** Invoked when the user confirms deletion ("edit" mode only). */
  onDelete?: () => void;
  /** Invoked when the user clicks the "All Notes" button ("edit" mode only). */
  onViewAll?: () => void;
  /** Invoked when the user closes/cancels the modal. */
  onCancel?: () => void;
  /** Text the annotation refers to; shown as a quoted preview at the top of the modal. */
  annotatedText?: string;
}

/**
 * Modal for creating or editing an annotation. A thin dialog shell around the
 * shared {@link NoteForm} body.
 *
 * Clicking Delete (edit mode) swaps the body for {@link DeleteAnnotationConfirmForm}
 * in place — the same dialog, retitled — rather than stacking a second modal.
 *
 * Modes:
 * - "create" — user is creating a new annotation
 * - "edit"   — editable display with Save / Cancel / Delete / All Notes actions
 */
function NoteModal({
  mode,
  annotation,
  onSave,
  onEdit,
  onDelete,
  onViewAll,
  onCancel,
  annotatedText,
}: NoteModalProps) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const isConfirmingDelete = confirmingDelete && !!annotation;
  const titleRef = useRef<HTMLDivElement>(null);

  // When the body swaps to the delete confirmation, move focus to the retitled
  // heading so screen readers announce the new context instead of being
  // stranded on the Delete button that just unmounted. Deferred a tick so it
  // lands after react-bootstrap's own focus handling settles.
  useEffect(() => {
    if (!isConfirmingDelete) return undefined;
    const id = setTimeout(() => titleRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [isConfirmingDelete]);

  return (
    <Modal
      show
      onHide={onCancel}
      centered
      className="noteModal"
      backdropClassName="noteModal-backdrop"
      restoreFocus={true}
    >
      <Modal.Header closeButton>
        <Modal.Title
          ref={titleRef}
          tabIndex={-1}
          className="noteModal__title font-weight-bold"
        >
          {isConfirmingDelete
            ? `Delete ${getAnnotationLabel(annotation!.type)}`
            : TITLES[mode]}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="d-flex flex-column pt-0">
        {isConfirmingDelete ? (
          <DeleteAnnotationConfirmForm
            annotation={annotation!}
            onConfirm={() => onDelete?.()}
            onCancel={() => setConfirmingDelete(false)}
          />
        ) : (
          <NoteForm
            mode={mode}
            annotation={annotation}
            annotatedText={annotatedText}
            onSave={onSave}
            onEdit={onEdit}
            onDeleteRequest={
              onDelete ? () => setConfirmingDelete(true) : undefined
            }
            onViewAll={onViewAll}
            onCancel={onCancel}
          />
        )}
      </Modal.Body>
    </Modal>
  );
}

export default NoteModal;
